import React, { useState, useEffect, useCallback } from 'react';
import {
  Watch, Heart, Footprints, Moon, Flame, Wind, Activity,
  RefreshCw, Plus, X, Wifi, WifiOff, Clock, TrendingUp, Zap, Timer
} from 'lucide-react';
import { COLORS } from '../data/mockData';
import {
  fetchWearableDevices, connectWearable, disconnectWearable,
  syncWearableData, fetchWearableSummary
} from '../services/api';

const METRIC_ICONS = {
  heart_rate: Heart, steps: Footprints, hrv: Activity, sleep_hours: Moon,
  calories: Flame, spo2: Wind, resting_hr: Heart, active_minutes: Timer,
};

const METRIC_COLORS = {
  heart_rate: '#EF5350', steps: '#4CAF50', hrv: '#7E57C2', sleep_hours: '#5C6BC0',
  calories: '#FF9800', spo2: '#26A69A', resting_hr: '#E91E63', active_minutes: '#42A5F5',
};

const METRIC_BG = {
  heart_rate: '#FFEBEE', steps: '#E8F5E9', hrv: '#EDE7F6', sleep_hours: '#E8EAF6',
  calories: '#FFF3E0', spo2: '#E0F2F1', resting_hr: '#FCE4EC', active_minutes: '#E3F2FD',
};

const AVAILABLE_DEVICES = [
  { name: 'Apple Watch Series 9', type: 'smartwatch' },
  { name: 'Fitbit Charge 6', type: 'fitness_tracker' },
  { name: 'Garmin Venu 3', type: 'smartwatch' },
  { name: 'Oura Ring Gen 3', type: 'ring' },
  { name: 'Whoop 4.0', type: 'band' },
  { name: 'Samsung Galaxy Watch 6', type: 'smartwatch' },
];

export default function WearablesPage() {
  const [devices, setDevices] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [devs, sum] = await Promise.all([fetchWearableDevices(), fetchWearableSummary()]);
      setDevices(devs);
      setSummary(sum);
    } catch {
      // Wearable data not available yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleConnect = async (device) => {
    try {
      const dev = await connectWearable(device.name, device.type);
      setDevices(prev => [...prev, dev]);
      setShowConnect(false);
    } catch (err) {
      console.error('Connect failed', err);
    }
  };

  const handleDisconnect = async (deviceId) => {
    try {
      await disconnectWearable(deviceId);
      setDevices(prev => prev.filter(d => d.device_id !== deviceId));
      setSummary({});
    } catch (err) {
      console.error('Disconnect failed', err);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const result = await syncWearableData();
      setSyncMessage(result.message || `Synced ${result.reading_count} readings`);
      const sum = await fetchWearableSummary();
      setSummary(sum);
    } catch (err) {
      setSyncMessage(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const hasDevices = devices.length > 0;
  const hasData = Object.keys(summary).length > 0;

  const mainMetrics = [
    { key: 'heart_rate', label: 'Heart Rate', icon: Heart, suffix: 'bpm' },
    { key: 'steps', label: 'Steps', icon: Footprints, suffix: '' },
    { key: 'hrv', label: 'HRV', icon: Activity, suffix: 'ms' },
    { key: 'sleep_hours', label: 'Sleep', icon: Moon, suffix: 'hrs' },
    { key: 'calories', label: 'Calories', icon: Flame, suffix: 'kcal' },
    { key: 'spo2', label: 'SpO2', icon: Wind, suffix: '%' },
    { key: 'resting_hr', label: 'Resting HR', icon: Heart, suffix: 'bpm' },
    { key: 'active_minutes', label: 'Active Min', icon: Timer, suffix: 'min' },
  ];

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: COLORS.textSecondary }}>Loading wearable data...</div>;

  return (
    <div style={{ maxWidth: 840, margin: '0 auto', padding: '16px 24px 32px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>Wearable Data</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {hasDevices && (
            <button onClick={handleSync} disabled={syncing} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 9999,
              backgroundColor: COLORS.primary, border: 'none', cursor: 'pointer', opacity: syncing ? 0.7 : 1,
            }}>
              <RefreshCw size={16} color="#FFF" className={syncing ? 'spin' : ''} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#FFF' }}>{syncing ? 'Syncing...' : 'Sync'}</span>
            </button>
          )}
          <button onClick={() => setShowConnect(true)} style={{
            width: 44, height: 44, borderRadius: 22, backgroundColor: COLORS.surfaceSecondary,
            display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer',
          }}>
            <Plus size={22} color={COLORS.primary} />
          </button>
        </div>
      </div>

      {syncMessage && (
        <div style={{ backgroundColor: '#E8F5E9', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Wifi size={16} color={COLORS.success} />
          <span style={{ fontSize: 14, color: COLORS.success, fontWeight: 500 }}>{syncMessage}</span>
        </div>
      )}

      {/* Connected Devices */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>Connected Devices</h2>
        {!hasDevices ? (
          <div style={{ textAlign: 'center', backgroundColor: COLORS.surface, borderRadius: 16, padding: '40px 24px' }}>
            <Watch size={48} color={COLORS.border} />
            <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginTop: 12, fontFamily: 'Outfit, sans-serif' }}>No Devices Connected</h3>
            <p style={{ fontSize: 14, color: COLORS.textSecondary, maxWidth: 300, margin: '8px auto 16px' }}>
              Connect a wearable device to start tracking your health metrics
            </p>
            <button onClick={() => setShowConnect(true)} style={{
              backgroundColor: COLORS.primary, borderRadius: 9999, padding: '12px 24px',
              border: 'none', cursor: 'pointer',
            }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#FFF' }}>Connect Device</span>
            </button>
          </div>
        ) : (
          devices.map(dev => (
            <div key={dev.device_id} style={{
              display: 'flex', alignItems: 'center', gap: 12, backgroundColor: COLORS.surface,
              borderRadius: 12, padding: 16, marginBottom: 8,
            }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Watch size={24} color={COLORS.secondary} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>{dev.device_name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <Wifi size={12} color={COLORS.success} />
                  <span style={{ fontSize: 12, color: COLORS.success, fontWeight: 500 }}>Connected</span>
                  {dev.last_synced && (
                    <>
                      <span style={{ fontSize: 12, color: COLORS.textSecondary }}> · </span>
                      <Clock size={12} color={COLORS.textSecondary} />
                      <span style={{ fontSize: 12, color: COLORS.textSecondary }}>
                        Last sync: {new Date(dev.last_synced).toLocaleTimeString()}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button onClick={() => handleDisconnect(dev.device_id)} style={{ padding: '8px 14px', borderRadius: 9999, border: `1px solid ${COLORS.error}`, backgroundColor: 'transparent', cursor: 'pointer' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.error }}>Disconnect</span>
              </button>
            </div>
          ))
        )}
      </div>

      {/* Metrics Grid */}
      {hasData && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>Latest Readings</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {mainMetrics.map(m => {
              const data = summary[m.key];
              if (!data) return null;
              const Icon = METRIC_ICONS[m.key] || Activity;
              const color = METRIC_COLORS[m.key] || COLORS.primary;
              const bg = METRIC_BG[m.key] || COLORS.surfaceSecondary;
              const trendValues = summary[`${m.key}_trend`];
              const avg = summary[`${m.key}_avg`];

              return (
                <div key={m.key} style={{
                  flex: '1 1 calc(50% - 6px)', minWidth: 180, backgroundColor: COLORS.surface,
                  borderRadius: 16, padding: 16, position: 'relative', overflow: 'hidden',
                }} className="goal-card">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={22} color={color} />
                    </div>
                    <span style={{ fontSize: 11, color: COLORS.textSecondary, fontWeight: 500 }}>{m.label}</span>
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>
                    {typeof data.value === 'number' ? (Number.isInteger(data.value) ? data.value : data.value.toFixed(1)) : data.value}
                    <span style={{ fontSize: 13, fontWeight: 500, color: COLORS.textSecondary, marginLeft: 4 }}>{m.suffix}</span>
                  </div>
                  {trendValues && trendValues.length > 1 && (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, marginTop: 8, height: 24 }}>
                      {trendValues.map((v, i) => {
                        const min = Math.min(...trendValues);
                        const max = Math.max(...trendValues);
                        const range = max - min || 1;
                        const h = Math.max(((v - min) / range) * 20, 3);
                        return <div key={i} style={{ flex: 1, height: h, backgroundColor: color, opacity: 0.3 + (i / trendValues.length) * 0.7, borderRadius: 2 }} />;
                      })}
                    </div>
                  )}
                  {avg !== undefined && (
                    <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 4 }}>
                      7-day avg: <span style={{ fontWeight: 600, color }}>{avg}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sync Prompt */}
      {hasDevices && !hasData && (
        <div style={{ textAlign: 'center', backgroundColor: COLORS.surface, borderRadius: 16, padding: '40px 24px' }}>
          <RefreshCw size={48} color={COLORS.primary} />
          <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginTop: 12, fontFamily: 'Outfit, sans-serif' }}>Ready to Sync</h3>
          <p style={{ fontSize: 14, color: COLORS.textSecondary, maxWidth: 300, margin: '8px auto 16px' }}>
            Tap sync to pull the latest health data from your connected device
          </p>
          <button onClick={handleSync} disabled={syncing} style={{
            backgroundColor: COLORS.primary, borderRadius: 9999, padding: '12px 24px',
            border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
            <RefreshCw size={16} color="#FFF" />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#FFF' }}>{syncing ? 'Syncing...' : 'Sync Now'}</span>
          </button>
        </div>
      )}

      {/* Connect Device Modal */}
      {showConnect && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 200, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ backgroundColor: COLORS.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, width: '100%', maxWidth: 500, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0 }}>Connect Device</h2>
              <button onClick={() => setShowConnect(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} color={COLORS.textPrimary} /></button>
            </div>
            <p style={{ fontSize: 14, color: COLORS.textSecondary, marginBottom: 16 }}>Select a wearable device to connect (mock integration)</p>
            {AVAILABLE_DEVICES.map((dev, i) => {
              const alreadyConnected = devices.some(d => d.device_name === dev.name);
              return (
                <button key={i} onClick={() => !alreadyConnected && handleConnect(dev)} disabled={alreadyConnected} style={{
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: 16,
                  backgroundColor: alreadyConnected ? COLORS.surfaceSecondary : COLORS.background,
                  borderRadius: 12, marginBottom: 8, border: 'none', cursor: alreadyConnected ? 'default' : 'pointer', textAlign: 'left',
                  opacity: alreadyConnected ? 0.6 : 1,
                }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Watch size={22} color={COLORS.secondary} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>{dev.name}</div>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary, textTransform: 'capitalize', marginTop: 2 }}>{dev.type.replace('_', ' ')}</div>
                  </div>
                  {alreadyConnected ? (
                    <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.success }}>Connected</span>
                  ) : (
                    <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.primary }}>Connect</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
